import type { ReactNode } from 'react'
import { ArrowRight, ChevronRight, Database, Sparkles } from 'lucide-react'
import { Sparkline } from '../charts/Sparkline'
import { WaterfallBridge } from '../charts/WaterfallBridge'
import { HealthRangeBar } from '../charts/HealthRangeBar'
import type {
  CfoAnalysisContext,
  CfoAnalysisVisualSpec,
  CfoInstantCard,
  CfoInstantCardChangeExplanation,
  CfoInstantCardComparisonRow,
  CfoInstantCardTrendPoint,
} from './types'

type CfoInstantCardDriver = CfoInstantCardChangeExplanation['drivers'][number]

// v2: bridge-driver clicks no longer echo the sibling waterfall — bumping the
// version keeps older persisted v1 cards (which carry the echo) from hydrating
// over freshly built cards.
const CARD_VERSION = 'cfo-instant-card-v2'
const DEFAULT_COMPACT_CONTEXT_BUDGET_BYTES = 8192

const trimStr = (value: unknown): string => String(value ?? '').trim()

const toFiniteNumber = (value: unknown): number | null => {
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

const titleCase = (value: string): string =>
  value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())

export const compactMoney = (value: number): string => {
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`
  return `${sign}$${abs.toFixed(0)}`
}

const compactNumber = (value: number): string => {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  if (Math.abs(value) < 10 && !Number.isInteger(value)) return value.toFixed(1)
  return value.toFixed(0)
}

const formatRawValue = (value: number | null, label: string): string | null => {
  if (value == null) return null
  const l = label.toLowerCase()
  if (l.includes('margin') || l.includes('rate') || l.includes('%')) return `${(value * (Math.abs(value) <= 1 ? 100 : 1)).toFixed(1)}%`
  if (l.includes('day') || l.includes('dso') || l.includes('dio') || l.includes('dpo')) return `${value.toFixed(0)}d`
  if (
    l.includes('revenue') ||
    l.includes('income') ||
    l.includes('cash') ||
    l.includes('expense') ||
    l.includes('cogs') ||
    l.includes('opex') ||
    l.includes('a/r') ||
    l.includes('receivable') ||
    l.includes('reserve')
  ) {
    return compactMoney(value)
  }
  return compactNumber(value)
}

const formatCardValue = (value: number | null | undefined, label: string): string => {
  if (value == null || !Number.isFinite(value)) return '-'
  return formatRawValue(value, label) || compactNumber(value)
}

const formatSignedPct = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '-'
  const sign = value > 0 ? '+' : value < 0 ? '-' : ''
  return `${sign}${Math.abs(value * 100).toFixed(1)}%`
}

const changeTone = (value: number | null | undefined): CfoInstantCardComparisonRow['tone'] => {
  if (value == null || !Number.isFinite(value) || Math.abs(value) < 0.000001) return 'neutral'
  return value > 0 ? 'good' : 'bad'
}

const periodKindFromLabel = (label: string): CfoInstantCardChangeExplanation['periodKind'] => {
  if (/last\s*3|trailing\s*3|3\s*(mo|month)/i.test(label)) return 'last_3_months'
  if (/ytd|year\s*to\s*date/i.test(label)) return 'ytd'
  if (/prior\s*year|year\s*over\s*year|yoy|last\s*12/i.test(label)) return 'prior_year'
  return 'current_month'
}

const changeDirectionFromText = (value: string): 'up' | 'down' | 'flat' => {
  const text = trimStr(value).replace(/[−–—]/g, '-')
  if (!text) return 'flat'
  const numeric = text.match(/\(?\s*([+-]?\d[\d,]*(?:\.\d+)?)/)
  if (numeric) {
    const parsed = Number(numeric[1].replace(/,/g, ''))
    if (Number.isFinite(parsed) && Math.abs(parsed) > 0.000001) {
      if (text.includes('(') && text.includes(')')) return 'down'
      return parsed < 0 ? 'down' : 'up'
    }
  }
  if (text.startsWith('-') || /down|declin|fell|lower|worse|compress/i.test(text)) return 'down'
  if (text.startsWith('+') || /up|increas|rose|higher|better|expand/i.test(text)) return 'up'
  return 'flat'
}

const fact = (label: string, value: unknown, tone?: CfoInstantCard['primaryMetric']['tone']) => {
  const text = trimStr(value)
  return text ? { label, value: text, tone } : null
}

const COMPARISON_SHORT: Record<string, string> = {
  mom: 'MoM',
  qoq: 'QoQ',
  yoy: 'YoY',
  ytd: 'YTD',
  l3m_vs_prior3m: 'vs last 3 mo',
  l12m_vs_prior12m: 'vs last 12 mo',
  prior_month: 'MoM',
  prior_year: 'YoY',
}

export const comparisonShortLabel = (value: unknown): string => {
  const key = trimStr(value).toLowerCase()
  if (!key) return ''
  return COMPARISON_SHORT[key] || trimStr(value)
}

const pctShare = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return ''
  return `${Math.round(Math.abs(value) * 100)}%`
}

/** Bridge-driver metrics whose next level is a real statement/segment recipe. */
export const CFO_BRIDGE_DRIVER_NEXT_LEVEL_METRICS: ReadonlySet<string> = new Set([
  'revenue',
  'sales',
  'topline',
  'cost_of_sales',
  'cogs',
  'cost_of_goods_sold',
  'cost_of_goods',
  'operating_expenses',
  'opex',
  'expenses',
])

/**
 * A driver-bar click on the net-income bridge (source_grain 'bridge_driver')
 * threads the bridge's OWN Revenue/COGS/Opex decomposition in
 * `selection.drivers` — those are the clicked line's SIBLINGS, not what is
 * inside it. Rendering them (plus the Prior→This NI anchors) repeats the exact
 * waterfall the user just clicked. For these clicks the card skips both; the
 * analysis modal fetches the true next level (segment contribution or
 * statement category/account movement) and grafts it onto the card.
 */
export const isCfoBridgeDriverNextLevelContext = (context: CfoAnalysisContext): boolean => {
  const selection = context.selection || {}
  if (trimStr((selection as any).source_grain) !== 'bridge_driver') return false
  const metric = trimStr(context.metricId || (selection as any).metric_id).toLowerCase()
  return CFO_BRIDGE_DRIVER_NEXT_LEVEL_METRICS.has(metric)
}

/**
 * Map the real sibling/sub-component drivers threaded onto the click selection
 * (`selection.drivers[]`, each `{ id, label, net_income_effect,
 * net_income_effect_display, metric_delta_abs, ... }`) into a ranked driver
 * list with a computed share-of-change. Drivers are ranked by the absolute
 * net-income effect (the favorability-signed dollar impact), share is each
 * driver's |effect| over the abs-sum of all effects (guarded for near-zero
 * totals so a help+drag that roughly cancel never reports a fake dominant
 * cause), and `direction` is favorable (`up`) / unfavorable (`down`).
 */
/**
 * Decompose a ratio/insight metric into its real constituent inputs from the
 * client-side grounding (numerator/denominator components with first→last and
 * top accounts). Used when the click site did not thread explicit
 * `selection.drivers[]` (e.g. the Ratios surface) so those cards still lead with
 * a real decomposition. Share-of-change is intentionally left null because a
 * ratio's inputs do not sum to its change, so no false "dominant cause" is shown.
 */
function buildDriversFromGroundingComponents(context: CfoAnalysisContext): CfoInstantCardDriver[] {
  const components = context.grounding?.components
  if (!Array.isArray(components) || !components.length) return []
  const surface = trimStr(context.activeSection) || trimStr(context.pageId) || null
  const metricId = trimStr(context.metricId || context.grounding?.metric_id) || null
  const mapped = components
    .map((component: any): CfoInstantCardDriver | null => {
      const first = toFiniteNumber(component?.first)
      const last = toFiniteNumber(component?.last)
      const deltaPct = toFiniteNumber(component?.delta_pct)
      const rawValue = first != null && last != null ? last - first : deltaPct
      const label = trimStr(component?.label) || titleCase(trimStr(component?.role))
      if (rawValue == null || !label) return null
      const display = first != null && last != null ? compactMoney(last - first) : formatSignedPct(deltaPct)
      return {
        id: `component:${normalizeKeyPart(label)}`,
        label,
        value: display,
        rawValue,
        shareOfChange: null,
        direction: rawValue > 0.000001 ? 'up' : rawValue < -0.000001 ? 'down' : 'flat',
        category: 'component',
        sourceSurface: surface,
        target: { surface, metricId },
      }
    })
    .filter((driver): driver is CfoInstantCardDriver => driver != null)
  mapped.sort((a, b) => Math.abs(Number(b.rawValue) || 0) - Math.abs(Number(a.rawValue) || 0))
  return mapped.slice(0, 6)
}

export function buildContributionDriversFromSelection(
  context: CfoAnalysisContext,
): CfoInstantCardDriver[] {
  const selection = context.selection || {}
  const raw: any[] | null =
    Array.isArray((selection as any).drivers) && !isCfoBridgeDriverNextLevelContext(context)
      ? ((selection as any).drivers as any[])
      : null
  if (!raw) return buildDriversFromGroundingComponents(context)
  const surface = trimStr(context.activeSection) || trimStr(context.pageId) || null
  const mapped = raw
    .map((driver: any): CfoInstantCardDriver | null => {
      const rawValue =
        toFiniteNumber(driver?.net_income_effect) ??
        toFiniteNumber(driver?.netIncomeEffect) ??
        toFiniteNumber(driver?.metric_delta_abs) ??
        toFiniteNumber(driver?.delta)
      const label = trimStr(driver?.label) || trimStr(driver?.metric_label) || titleCase(trimStr(driver?.id))
      if (rawValue == null || !label) return null
      const display = trimStr(driver?.net_income_effect_display) || trimStr(driver?.metric_delta_abs_display) || compactMoney(rawValue)
      const metricId = trimStr(driver?.id || driver?.metric_id) || null
      return {
        id: metricId || `driver:${normalizeKeyPart(label)}`,
        label,
        value: display,
        rawValue,
        shareOfChange: null,
        direction: rawValue > 0.000001 ? 'up' : rawValue < -0.000001 ? 'down' : 'flat',
        category: trimStr(driver?.category) || 'bridge_driver',
        sourceSurface: surface,
        target: {
          surface,
          metricId,
          accountId: trimStr(driver?.account_id) || null,
          customerId: trimStr(driver?.customer_id) || null,
        },
      }
    })
    .filter((driver): driver is CfoInstantCardDriver => driver != null)
  const absSum = mapped.reduce((sum, driver) => sum + Math.abs(Number(driver.rawValue) || 0), 0)
  if (absSum > 0.000001) {
    for (const driver of mapped) driver.shareOfChange = Math.abs(Number(driver.rawValue) || 0) / absSum
  }
  mapped.sort((a, b) => Math.abs(Number(b.rawValue) || 0) - Math.abs(Number(a.rawValue) || 0))
  return mapped.slice(0, 6)
}

type DriverConcentration = {
  top: CfoInstantCardDriver | null
  second: CfoInstantCardDriver | null
  topShare: number | null
  /** One driver explains ≥ half the move. */
  dominant: boolean
  /** Several drivers of comparable size — no single cause (muddy month). */
  mixed: boolean
}

const DOMINANCE_THRESHOLD = 0.5

const driverConcentration = (drivers: CfoInstantCardDriver[]): DriverConcentration => {
  const top = drivers[0] || null
  const second = drivers[1] || null
  const topShare = top?.shareOfChange ?? null
  const dominant = !!top && topShare != null && topShare >= DOMINANCE_THRESHOLD
  // "mixed" (a muddy month with offsetting drivers) only applies when shares are
  // actually computed and sum to the move. Component decompositions (e.g. a
  // ratio's numerator/denominator) carry null shares and do not sum, so they are
  // never treated as mixed — the grade stays driven by the metric's own tone.
  const mixed = drivers.length >= 2 && !dominant && topShare != null
  return { top, second, topShare, dominant, mixed }
}

/**
 * Compute the answer-first graded verdict from the selected metric's tone +
 * magnitude and the ranked drivers. Honest on muddy months: when no single
 * driver clears the dominance threshold the grade softens to `watch` and the
 * headline omits any single-cause claim rather than overstating dominance.
 */
const computeVerdict = (args: {
  metricLabel: string
  comparisonLabel: string
  tone: CfoInstantCard['primaryMetric']['tone']
  deltaRaw: number | null
  deltaDisplay: string | null
  drivers: CfoInstantCardDriver[]
  bridgeContext: boolean
}): NonNullable<CfoInstantCard['verdict']> => {
  const { metricLabel, comparisonLabel, tone, deltaRaw, deltaDisplay, drivers, bridgeContext } = args
  const conc = driverConcentration(drivers)
  let grade: NonNullable<CfoInstantCard['verdict']>['grade'] =
    tone === 'bad' ? 'risk' : tone === 'good' ? 'good' : tone === 'watch' ? 'watch' : 'neutral'
  // Muddy month: real movement but no dominant cause → soften a risk/good read.
  if (conc.mixed && (grade === 'risk' || grade === 'good')) grade = 'watch'
  const dirWord = deltaRaw == null ? 'moved' : deltaRaw < 0 ? 'fell' : deltaRaw > 0 ? 'rose' : 'held flat'
  const mag = trimStr(deltaDisplay) || (deltaRaw != null ? compactMoney(Math.abs(deltaRaw)) : '')
  const short = comparisonShortLabel(comparisonLabel)
  const base = `${metricLabel} ${dirWord}${mag ? ` ${mag}` : ''}${short ? ` ${short}` : ''}`
  let clause = ''
  if (conc.top) {
    const selectedIsTop = trimStr(conc.top.label).toLowerCase() === trimStr(metricLabel).toLowerCase()
    if (bridgeContext && selectedIsTop) {
      clause = ` — the single largest ${conc.top.direction === 'up' ? 'lift to' : 'drag on'} net income`
    } else if (conc.dominant && !selectedIsTop) {
      clause = ` — ${conc.top.label} drove ~${pctShare(conc.topShare)} of it`
    } else if (conc.mixed) {
      clause = ' — a mix of offsetting drivers, no single cause'
    }
  }
  const headline = `${base}${clause}.`
  // Substance only: the comparison window already lives in the headline and the
  // period badge, so a lone "vs MoM" line is dropped rather than echoed again.
  const substance = [
    mag || null,
    conc.dominant && conc.top && trimStr(conc.top.label).toLowerCase() !== trimStr(metricLabel).toLowerCase()
      ? `~${pctShare(conc.topShare)} from ${conc.top.label}`
      : null,
  ].filter(Boolean)
  const magnitudeLine = substance.length
    ? [...substance, comparisonLabel ? `vs ${short || comparisonLabel}` : null].filter(Boolean).join(' · ')
    : null
  return { grade, headline, magnitudeLine }
}

/**
 * Resolve the optional hero-bridge anchors (Prior NI → This NI) from the click
 * selection. Present only on net-income-bridge surfaces; absent elsewhere so the
 * ranked driver list stands alone as the hero.
 */
const buildBridgeAnchors = (context: CfoAnalysisContext): CfoInstantCard['bridge'] => {
  // A bridge-driver click already came FROM the waterfall — re-anchoring
  // Prior→This NI here would draw the same chart again inside the card.
  if (isCfoBridgeDriverNextLevelContext(context)) return null
  const selection = context.selection || {}
  const anchors = (selection as any).bridge_anchors
  if (anchors && toFiniteNumber(anchors?.start?.value) != null && toFiniteNumber(anchors?.end?.value) != null) {
    return {
      start: { label: trimStr(anchors.start.label) || 'Prior', value: Number(anchors.start.value) },
      end: { label: trimStr(anchors.end.label) || 'Current', value: Number(anchors.end.value) },
    }
  }
  const snap = (selection as any).net_income_snapshot
  if (snap && toFiniteNumber(snap?.prior_value) != null && toFiniteNumber(snap?.current_value) != null) {
    return {
      start: { label: 'Prior NI', value: Number(snap.prior_value) },
      end: { label: 'This NI', value: Number(snap.current_value) },
    }
  }
  return null
}

const normalizeKeyPart = (value: unknown): string =>
  trimStr(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'selected'

const selectionKeyFromContext = (context: CfoAnalysisContext): string => {
  const selection = context.selection || {}
  return trimStr((selection as any).selection_key) ||
    trimStr((selection as any).selectionKey) ||
    trimStr((selection as any).row_key) ||
    trimStr((selection as any).rowKey) ||
    trimStr((selection as any).account_id) ||
    trimStr((selection as any).customer_id) ||
    trimStr((selection as any).scenario_id) ||
    trimStr((selection as any).report_block_id)
}

const primaryFromVisual = (visual: CfoAnalysisVisualSpec | null | undefined, context: CfoAnalysisContext) => {
  if (visual?.kind === 'value') {
    return { label: visual.label, value: visual.display, rawValue: visual.value ?? null }
  }
  if (visual?.kind === 'ratio_health') {
    return {
      label: visual.label,
      value: visual.display,
      rawValue: visual.value,
      tone:
        visual.assessment === 'healthy'
          ? 'good'
          : visual.assessment === 'watch'
            ? 'watch'
            : visual.assessment === 'concern'
              ? 'bad'
              : 'neutral',
    } as CfoInstantCard['primaryMetric']
  }
  const grounding = context.grounding
  if (grounding?.display || grounding?.latest != null) {
    const label = trimStr(grounding.metric_label) || trimStr(context.metricLabel) || 'Selected metric'
    return {
      label,
      value: trimStr(grounding.display) || formatRawValue(grounding.latest, label) || compactNumber(Number(grounding.latest || 0)),
      rawValue: grounding.latest,
      tone:
        grounding.assessment === 'healthy'
          ? 'good'
          : grounding.assessment === 'watch'
            ? 'watch'
            : grounding.assessment === 'concern'
              ? 'bad'
              : 'neutral',
    } as CfoInstantCard['primaryMetric']
  }
  const selection = context.selection || {}
  const explicitTone = trimStr((selection as any).metric_tone)
  const selectedTone = (['good', 'bad', 'watch', 'neutral'].includes(explicitTone)
    ? explicitTone
    : null) as CfoInstantCard['primaryMetric']['tone']
  const selectedValue =
    toFiniteNumber(selection.net_income_effect) ??
    toFiniteNumber(selection.metric_delta) ??
    toFiniteNumber(selection.delta) ??
    toFiniteNumber(selection.value)
  const label = trimStr(context.metricLabel) || trimStr(selection.label) || 'Selected evidence'
  return {
    label,
    value: formatRawValue(selectedValue, label) || trimStr(selection.display) || 'Selected',
    rawValue: selectedValue,
    tone: selectedTone ?? (selectedValue == null ? 'neutral' : selectedValue < 0 ? 'bad' : selectedValue > 0 ? 'good' : 'neutral'),
  } as CfoInstantCard['primaryMetric']
}

const normalizeTrendPoints = (
  context: CfoAnalysisContext,
  visual: CfoAnalysisVisualSpec | null | undefined,
  metricLabel: string,
): CfoInstantCard['trend'] => {
  const selection = context.selection || {}
  const explicit = (selection as any).instant_card_trend || (selection as any).metric_trend
  const explicitPoints = Array.isArray(explicit?.points)
    ? explicit.points
    : Array.isArray(explicit?.trend)
      ? explicit.trend.map((value: unknown, index: number) => ({
        value,
        label: Array.isArray(explicit?.labels) ? explicit.labels[index] : null,
      }))
      : null
  const groundingPoints = Array.isArray(context.grounding?.trend)
    ? context.grounding.trend.map((point) => ({
      label: point.period,
      period: point.period,
      rawValue: point.value,
      value: formatCardValue(point.value, metricLabel),
    }))
    : null
  const visualPoints = visual?.kind === 'ratio_health' && Array.isArray(visual.trend)
    ? visual.trend.map((value, index) => ({
      label: `M-${visual.trend!.length - index - 1}`,
      rawValue: value,
      value: formatCardValue(value, metricLabel),
    }))
    : null
  const rawPoints = explicitPoints || groundingPoints || visualPoints || []
  const points = rawPoints
    .map((point: any, index: number): CfoInstantCardTrendPoint | null => {
      const rawValue = toFiniteNumber(point?.rawValue ?? point?.value)
      const valueText = trimStr(point?.display ?? point?.value_display ?? point?.formatted ?? (rawValue == null ? null : formatCardValue(rawValue, metricLabel)))
      const label = trimStr(point?.label ?? point?.period) || `M-${rawPoints.length - index - 1}`
      if (!valueText && rawValue == null) return null
      return { label, value: valueText || formatCardValue(rawValue, metricLabel), rawValue, period: trimStr(point?.period) || null }
    })
    .filter((point: CfoInstantCardTrendPoint | null): point is CfoInstantCardTrendPoint => point != null)
    .slice(-12)
  const hasFullMonthlySeries = points.length >= 12
  return {
    label: hasFullMonthlySeries
      ? (trimStr(explicit?.label) || '12-month trend')
      : trimStr(explicit?.label) || (points.length >= 2 ? `${points.length}-month trend` : 'Trend'),
    points,
    unavailableReason: hasFullMonthlySeries
      ? null
      : trimStr(explicit?.unavailable_reason) || (points.length ? 'A full monthly 12-point trend was not included in this page fact.' : 'Trend series was not included in this page fact yet.'),
  }
}

const normalizeComparisonRows = (
  context: CfoAnalysisContext,
  metricLabel: string,
): CfoInstantCardComparisonRow[] => {
  const selection = context.selection || {}
  const explicit = (selection as any).instant_card_comparisons || (selection as any).comparison_rows
  const rows = Array.isArray(explicit)
    ? explicit
    : Array.isArray(context.grounding?.comparisons)
      ? context.grounding.comparisons.map((entry) => ({
        label: entry.label,
        current: formatCardValue(entry.current, metricLabel),
        prior: formatCardValue(entry.prior, metricLabel),
        change: formatSignedPct(entry.delta_pct),
        tone: changeTone(entry.delta_pct),
      }))
      : []
  const byKey = new Map<string, CfoInstantCardComparisonRow>()
  for (const row of rows) {
    const label = trimStr(row?.label)
    const canonical = /(?:last|trailing)\s*3|3\s*(?:mo|month)|l3m/i.test(label)
      ? 'Last 3 months'
      : /ytd|year\s*to\s*date/i.test(label)
        ? 'YTD'
        : /prior\s*year|year\s*over\s*year|yoy|last\s*12|l12m/i.test(label)
          ? 'Prior year'
          : /current\s*month|^month$|mom/i.test(label)
            ? 'Current month'
            : label
    if (!canonical) continue
    const current = trimStr(row?.current) || '-'
    const prior = trimStr(row?.prior) || '-'
    const change = trimStr(row?.change) || trimStr(row?.delta) || '-'
    const unavailableReason = trimStr(row?.unavailableReason || row?.unavailable_reason)
    byKey.set(canonical, {
      label: canonical,
      current,
      prior,
      change,
      tone: unavailableReason || (current === '-' && prior === '-' && change === '-') ? 'neutral' : row?.tone || null,
      unavailableReason: unavailableReason || (current === '-' && prior === '-' && change === '-' ? 'Not available in this page fact.' : null),
    })
  }
  return ['Current month', 'Last 3 months', 'YTD', 'Prior year'].map((label) => (
    byKey.get(label) || { label, current: '-', prior: '-', change: '-', tone: 'neutral', unavailableReason: 'Not available in this page fact.' }
  ))
}

// The movement the drivers explain: in a net-income-bridge context the clicked
// line (Revenue/COGS/Opex) is itself a driver OF net income, so "the net-income
// move" reads correctly; elsewhere the drivers decompose the metric's own move.
const moveNoun = (metricLabel: string, bridgeContext: boolean): string =>
  bridgeContext ? 'the net-income move' : `the ${metricLabel} move`

const driverHeadline = (metricLabel: string, drivers: CfoInstantCardDriver[], bridgeContext: boolean): string => {
  const conc = driverConcentration(drivers)
  const move = moveNoun(metricLabel, bridgeContext)
  if (conc.dominant && conc.top) {
    return `${conc.top.label} drove ~${pctShare(conc.topShare)} of ${move} (${conc.top.value}).`
  }
  if (conc.mixed && conc.top && conc.second) {
    return `Offsetting drivers — ${conc.top.label} (${conc.top.value}) and ${conc.second.label} (${conc.second.value}) roughly cancel.`
  }
  if (conc.top) {
    return `${conc.top.label} (${conc.top.value}) was the largest driver of ${move}.`
  }
  return `${metricLabel} drivers are surfaced below.`
}

const driverRecommendedAction = (metricLabel: string, drivers: CfoInstantCardDriver[]): string => {
  const conc = driverConcentration(drivers)
  if (conc.top && trimStr(conc.top.label).toLowerCase() !== trimStr(metricLabel).toLowerCase()) {
    return `Break ${conc.top.label} down to confirm whether the ${conc.top.value} swing is one account/customer or broad before changing the forecast.`
  }
  if (conc.top) {
    return `Confirm whether the ${metricLabel} move is recurring, timing-related, or one-off before changing the forecast or monthly narrative.`
  }
  return `Inspect the largest ${metricLabel} driver and decide whether it changes this month's operating plan.`
}

const normalizeChangeExplanations = (
  context: CfoAnalysisContext,
  comparisonRows: CfoInstantCardComparisonRow[],
  metricLabel: string,
  drivers: CfoInstantCardDriver[],
  bridgeContext: boolean,
): CfoInstantCardChangeExplanation[] => {
  const selection = context.selection || {}
  const explicit = (selection as any).instant_card_change_explanations || (selection as any).change_explanations
  const normalizedExplicit = Array.isArray(explicit)
    ? explicit
      .map((item: any): CfoInstantCardChangeExplanation | null => {
        const label = trimStr(item?.label)
        const periodKind = trimStr(item?.periodKind || item?.period_kind) || periodKindFromLabel(label)
        const headline = trimStr(item?.headline)
        const itemDrivers = Array.isArray(item?.drivers)
          ? item.drivers
            .map((driver: any, index: number) => ({
              id: trimStr(driver?.id) || `${periodKind}:driver:${index}`,
              label: trimStr(driver?.label),
              value: trimStr(driver?.value),
              rawValue: toFiniteNumber(driver?.rawValue ?? driver?.raw_value),
              shareOfChange: toFiniteNumber(driver?.shareOfChange ?? driver?.share_of_change),
              direction: (['up', 'down', 'flat'].includes(trimStr(driver?.direction)) ? trimStr(driver?.direction) : changeDirectionFromText(trimStr(driver?.value))) as 'up' | 'down' | 'flat',
              category: trimStr(driver?.category) || null,
              sourceSurface: trimStr(driver?.sourceSurface || driver?.source_surface) || null,
              target: driver?.target ?? null,
            }))
            .filter((driver: any) => driver.label && driver.value)
            .slice(0, 6)
          : []
        if (!label || !headline) return null
        return {
          periodKind,
          label,
          status: (['ready', 'partial', 'unsupported', 'stale'].includes(trimStr(item?.status)) ? trimStr(item?.status) : 'ready') as CfoInstantCardChangeExplanation['status'],
          current: trimStr(item?.current) || '-',
          prior: trimStr(item?.prior) || '-',
          change: trimStr(item?.change) || '-',
          headline,
          drivers: itemDrivers,
          confidence: (['high', 'medium', 'low'].includes(trimStr(item?.confidence)) ? trimStr(item?.confidence) : 'medium') as CfoInstantCardChangeExplanation['confidence'],
          caveats: Array.isArray(item?.caveats) ? item.caveats.map(trimStr).filter(Boolean).slice(0, 3) : undefined,
          recommendedAction: trimStr(item?.recommendedAction || item?.recommended_action) || undefined,
        }
      })
      .filter((item): item is CfoInstantCardChangeExplanation => item != null)
    : []
  if (normalizedExplicit.length) {
    // Collapse to the single current_month explanation (or the first supplied) —
    // never a four-window fan-out.
    const currentMonth = normalizedExplicit.find((item) => item.periodKind === 'current_month') || normalizedExplicit[0]
    return currentMonth ? [currentMonth] : []
  }

  // No explicit explanations: build ONE current_month explanation from the real
  // ranked drivers. When there are no drivers, return nothing (the renderer shows
  // an honest "drivers warming" state) — never echo the comparison windows.
  if (!drivers.length) return []
  const currentRow = comparisonRows.find((row) => row.label === 'Current month')
  const change = trimStr(currentRow?.change) || drivers[0]?.value || '-'
  return [{
    periodKind: 'current_month',
    label: 'Current month',
    status: 'ready',
    current: trimStr(currentRow?.current) || '-',
    prior: trimStr(currentRow?.prior) || '-',
    change,
    headline: driverHeadline(metricLabel, drivers, bridgeContext),
    drivers,
    confidence: 'high',
    recommendedAction: driverRecommendedAction(metricLabel, drivers),
  }]
}

const insightSeeds = (
  context: CfoAnalysisContext,
  deterministicRead: string,
  metricLabel: string,
  drivers: CfoInstantCardDriver[],
  bridgeContext: boolean,
): string[] => {
  const selection = context.selection || {}
  const explicit = (selection as any).instant_card_insights
  if (Array.isArray(explicit)) {
    const cleaned = explicit.map(trimStr).filter(Boolean)
    if (cleaned.length) return cleaned.slice(0, 3)
  }
  const conc = driverConcentration(drivers)
  const move = moveNoun(metricLabel, bridgeContext)
  if (conc.dominant && conc.top) {
    const second = conc.second ? ` ${conc.second.label} was a distant ${pctShare(conc.second.shareOfChange)}.` : ''
    return [`${conc.top.label} drove ~${pctShare(conc.topShare)} of ${move} (${conc.top.value}).${second}`]
  }
  if (conc.mixed && conc.top && conc.second) {
    return [`No single line explains ${move}: ${conc.top.label} (${conc.top.value}) and ${conc.second.label} (${conc.second.value}) roughly offset, so the net move is muted.`]
  }
  return [deterministicRead].filter(Boolean).slice(0, 1)
}

const prescriptiveActionSeeds = (
  context: CfoAnalysisContext,
  metricLabel: string,
  drivers: CfoInstantCardDriver[],
): string[] => {
  const selection = context.selection || {}
  const explicit = (selection as any).instant_card_prescriptive_actions
  if (Array.isArray(explicit)) {
    const cleaned = explicit.map(trimStr).filter(Boolean)
    if (cleaned.length) return cleaned.slice(0, 3)
  }
  return [driverRecommendedAction(metricLabel, drivers)]
}

export const buildCfoInstantCardKey = (context: CfoAnalysisContext): string =>
  [
    normalizeKeyPart(context.activeSection || context.pageId),
    normalizeKeyPart(context.sourceComponent || 'evidence'),
    normalizeKeyPart(context.metricId || context.metricLabel),
    normalizeKeyPart(context.period || context.asOfLabel),
    normalizeKeyPart(context.comparison || 'current'),
    selectionKeyFromContext(context) ? normalizeKeyPart(selectionKeyFromContext(context)) : null,
  ].filter(Boolean).join(':')

export function buildCfoInstantCardFromContext({
  context,
  visual,
}: {
  context: CfoAnalysisContext
  visual?: CfoAnalysisVisualSpec | null
}): CfoInstantCard {
  const primaryMetric = primaryFromVisual(visual, context)
  const selection = context.selection || {}
  const grounding = context.grounding || null
  const comparisonLabel = trimStr(context.comparison) || trimStr((selection as any).comparison_label)
  const periodLabel = trimStr(context.asOfLabel) || trimStr(context.period) || trimStr((selection as any).current_period_end)
  const sourceLabel = titleCase(trimStr(context.sourceComponent) || 'evidence')
  const metricLabel = trimStr(primaryMetric.label) || trimStr(context.metricLabel) || sourceLabel
  const delta = toFiniteNumber((selection as any).net_income_effect) ?? toFiniteNumber((selection as any).metric_delta) ?? toFiniteNumber((selection as any).delta)
  const bridgeEffect = delta == null ? null : compactMoney(delta)
  const facts = [
    fact('Period', periodLabel),
    fact('Comparison', comparisonLabel),
    fact('Source', sourceLabel),
    fact('Effect', bridgeEffect, delta == null ? null : delta < 0 ? 'bad' : delta > 0 ? 'good' : 'neutral'),
    fact('Healthy range', grounding?.healthy_min != null || grounding?.healthy_max != null ? `${grounding.healthy_min ?? 'min'} to ${grounding.healthy_max ?? 'max'}` : null),
    fact('Assessment', grounding?.assessment ? titleCase(grounding.assessment) : null, grounding?.assessment === 'concern' ? 'bad' : grounding?.assessment === 'watch' ? 'watch' : null),
  ].filter(Boolean) as CfoInstantCard['supportingFacts']
  const deterministicRead =
    trimStr(grounding?.interpretation) ||
    trimStr((selection as any).deterministic_read) ||
    (bridgeEffect
      ? `${metricLabel} contributed ${bridgeEffect} to the selected movement.`
      : `${metricLabel} is the selected evidence for this analysis.`)
  const trend = normalizeTrendPoints(context, visual, metricLabel)
  const comparisonRows = normalizeComparisonRows(context, metricLabel)
  const drivers = buildContributionDriversFromSelection(context)
  const bridgeContext =
    /bridge|net_income/i.test(trimStr((selection as any).source_grain)) || !!(selection as any).bridge_anchors
  const changeExplanations = normalizeChangeExplanations(context, comparisonRows, metricLabel, drivers, bridgeContext)
  const verdictDrivers = drivers.length ? drivers : (changeExplanations[0]?.drivers ?? [])
  // A click site that already knows the story (e.g. an insight card whose
  // headline IS the verdict) supplies it directly instead of having the card
  // re-derive a vacuous "<metric> moved MoM." from a delta it doesn't have.
  const explicitVerdict = (selection as any).instant_card_verdict
  const explicitVerdictGrade = trimStr(explicitVerdict?.grade)
  const verdict: NonNullable<CfoInstantCard['verdict']> = explicitVerdict && trimStr(explicitVerdict.headline)
    ? {
      grade: (['good', 'watch', 'risk', 'neutral'].includes(explicitVerdictGrade)
        ? explicitVerdictGrade
        : 'neutral') as NonNullable<CfoInstantCard['verdict']>['grade'],
      headline: trimStr(explicitVerdict.headline),
      magnitudeLine: trimStr(explicitVerdict.magnitudeLine ?? explicitVerdict.magnitude_line) || null,
    }
    : computeVerdict({
      metricLabel,
      comparisonLabel: comparisonLabel || trimStr(context.comparison),
      tone: primaryMetric.tone,
      deltaRaw: delta,
      deltaDisplay: bridgeEffect,
      drivers: verdictDrivers,
      bridgeContext,
    })
  const bridge = buildBridgeAnchors(context)
  const prescriptiveSeed = [
    deterministicRead,
    'Use the surfaced deterministic change explanations to decide what matters; do not request full page contracts for this first read.',
    changeExplanations.find((item) => item.status === 'ready' || item.status === 'partial')?.headline,
    bridgeEffect ? `Focus first on the ${metricLabel} movement and whether it is recurring, timing-related, or classification-related.` : null,
  ]
    .filter(Boolean)
    .join(' ')
  const actions = [
    {
      kind: 'drilldown',
      label: `Break down ${metricLabel}`,
      prompt: `Break down ${metricLabel} for ${periodLabel || 'this period'} and show the largest drivers only.`,
      target: {
        surface: trimStr(context.activeSection) || trimStr(context.pageId) || null,
        metricId: trimStr(context.metricId || grounding?.metric_id) || null,
        accountId: trimStr((selection as any).account_id) || null,
        customerId: trimStr((selection as any).customer_id) || null,
        scenarioId: trimStr((selection as any).scenario_id) || null,
      },
    },
    {
      kind: 'compare',
      label: 'Compare periods',
      prompt: `Compare ${metricLabel} across the selected comparison window and call out whether this is a one-period spike or a trend.`,
      target: {
        surface: trimStr(context.activeSection) || trimStr(context.pageId) || null,
        metricId: trimStr(context.metricId || grounding?.metric_id) || null,
      },
    },
    {
      kind: 'transactions',
      label: 'Inspect detail',
      prompt: `Open the next layer of detail behind ${metricLabel}, including accounts or transactions only if needed.`,
      target: {
        surface: trimStr(context.activeSection) || trimStr(context.pageId) || null,
        metricId: trimStr(context.metricId || grounding?.metric_id) || null,
        accountId: trimStr((selection as any).account_id) || null,
        customerId: trimStr((selection as any).customer_id) || null,
      },
    },
  ]
  const explicitNextSteps = Array.isArray((selection as any).instant_card_next_steps)
    ? (selection as any).instant_card_next_steps
      .map((step: any) => ({
        kind: trimStr(step?.kind) || 'ask',
        label: trimStr(step?.label),
        prompt: trimStr(step?.prompt),
        target: step?.target ?? null,
      }))
      .filter((step: any) => step.label && step.prompt)
    : null
  const nextSteps = explicitNextSteps?.length ? explicitNextSteps.slice(0, 4) : actions.slice(0, 3)
  return {
    cardKey: buildCfoInstantCardKey(context),
    selectionKey: selectionKeyFromContext(context) || null,
    surface: trimStr(context.activeSection) || trimStr(context.pageId) || 'cfo_core',
    cardKind: trimStr(context.sourceComponent) || visual?.kind || 'evidence',
    version: CARD_VERSION,
    title: metricLabel,
    subtitle: periodLabel
      ? `${periodLabel}${comparisonLabel ? ` · ${comparisonShortLabel(comparisonLabel)}` : ''}`
      : comparisonShortLabel(comparisonLabel) || null,
    period: context.period ?? null,
    comparison: context.comparison ?? null,
    primaryMetric,
    verdict,
    bridge,
    supportingFacts: facts.slice(0, 6),
    healthBand: (selection as any).instant_card_health_band ?? null,
    explain: (selection as any).instant_card_explain ?? null,
    trend,
    comparisons: comparisonRows,
    changeExplanations,
    insights: insightSeeds(context, deterministicRead, metricLabel, verdictDrivers, bridgeContext),
    prescriptiveActions: prescriptiveActionSeeds(context, metricLabel, verdictDrivers),
    nextSteps,
    deterministicRead,
    prescriptiveSeed,
    actions,
    sourceLineage: {
      sourceComponent: context.sourceComponent ?? null,
      metricId: context.metricId ?? grounding?.metric_id ?? null,
      sourceTables: context.sourceTables ?? null,
      artifactIds: Array.isArray((selection as any).artifact_ids) ? (selection as any).artifact_ids : null,
    },
    payloadBudget: {
      compactContextBytes: null,
      maxContextBytes: DEFAULT_COMPACT_CONTEXT_BUDGET_BYTES,
    },
    freshness: {
      sourceFingerprint: trimStr((selection as any).source_fingerprint || (selection as any).signature) || null,
      updatedAt: null,
      stale: null,
      staleReason: null,
    },
  }
}

export function compactInstantCardForAgent(card: CfoInstantCard | null | undefined): Record<string, unknown> | null {
  if (!card) return null
  const shortText = (value: unknown, max = 220): string => {
    const text = trimStr(value)
    return text.length > max ? `${text.slice(0, max - 1)}…` : text
  }
  const compactActions = (card.nextSteps?.length ? card.nextSteps : card.actions).slice(0, 4).map((action) => ({
    kind: action.kind ?? 'ask',
    label: shortText(action.label, 80),
    prompt: shortText(action.prompt, 180),
    target: action.target ?? null,
  }))
  return {
    card_key: card.cardKey,
    selection_key: card.selectionKey ?? null,
    surface: card.surface,
    card_kind: card.cardKind,
    version: card.version,
    title: card.title,
    period: card.period ?? null,
    comparison: card.comparison ?? null,
    primary_metric: card.primaryMetric,
    verdict: card.verdict
      ? {
        grade: card.verdict.grade,
        headline: shortText(card.verdict.headline, 200),
        magnitude_line: shortText(card.verdict.magnitudeLine, 120),
      }
      : null,
    supporting_facts: card.supportingFacts.slice(0, 3),
    trend: card.trend ? {
      label: card.trend.label ?? null,
      points: (card.trend.points || []).slice(-12).map((point) => ({
        label: point.label,
        value: point.value,
      })),
      unavailable_reason: card.trend.unavailableReason ?? null,
    } : null,
    comparisons: (card.comparisons || []).slice(0, 4),
    change_explanations: (card.changeExplanations || []).slice(0, 4).map((explanation) => ({
      period_kind: explanation.periodKind,
      label: explanation.label,
      status: explanation.status,
      current: shortText(explanation.current, 48),
      prior: shortText(explanation.prior, 48),
      change: shortText(explanation.change, 48),
      headline: shortText(explanation.headline, 180),
      drivers: (explanation.drivers || []).slice(0, 3).map((driver) => ({
        id: shortText(driver.id, 80),
        label: shortText(driver.label, 90),
        value: shortText(driver.value, 60),
        shareOfChange: driver.shareOfChange ?? null,
        direction: driver.direction,
        category: driver.category ?? null,
        target: driver.target ?? null,
      })),
      confidence: explanation.confidence,
      caveats: (explanation.caveats || []).slice(0, 2).map((item) => shortText(item, 120)),
      recommended_action: shortText(explanation.recommendedAction, 180),
    })),
    insights: (card.insights || []).slice(0, 3).map((item) => shortText(item)),
    prescriptive_actions: (card.prescriptiveActions || []).slice(0, 3).map((item) => shortText(item)),
    next_steps: compactActions,
    deterministic_read: shortText(card.deterministicRead),
    prescriptive_seed: shortText(card.prescriptiveSeed, 320),
    actions: compactActions.map((action) => ({
      kind: action.kind,
      label: action.label,
      target: action.target,
    })),
    source_lineage: {
      sourceComponent: card.sourceLineage.sourceComponent ?? null,
      metricId: card.sourceLineage.metricId ?? null,
      sourceTables: (card.sourceLineage.sourceTables || []).slice(0, 3),
      artifactIds: (card.sourceLineage.artifactIds || []).slice(0, 3),
    },
    payload_budget: card.payloadBudget ?? null,
    freshness: card.freshness,
  }
}

const GRADE_LABEL: Record<NonNullable<CfoInstantCard['verdict']>['grade'], string> = {
  good: 'On track',
  watch: 'Watch',
  risk: 'Risk',
  neutral: 'Read',
}

const driverInspectAction = (
  driver: CfoInstantCardDriver,
  metricLabel: string,
): CfoInstantCard['actions'][number] | null =>
  driver.target
    ? {
      kind: 'drilldown',
      label: `Inspect ${driver.label}`,
      prompt: `Inspect ${driver.label} (${driver.value}) as a surfaced driver behind ${metricLabel}; show the largest underlying accounts or transactions only.`,
      target: driver.target,
    }
    : null

/** A driver row can render in the prior→now movement grid when it carries both
 *  endpoints (segment-CM-style: Prior · dumbbell · Now). */
const hasMovementEndpoints = (driver: CfoInstantCardDriver): boolean =>
  trimStr(driver.priorDisplay ?? '') !== '' && trimStr(driver.currentDisplay ?? '') !== ''

/** Prior→now dumbbell on a shared 0..1 scale — hollow dot = prior, filled dot
 *  = now, connecting segment colored by direction. Lifted from the segment-CM
 *  panel so the modal reads the same as the Financials contribution widget. */
function MovementDumbbell({ prior, now, direction }: {
  prior: number | null | undefined
  now: number | null | undefined
  direction: CfoInstantCardDriver['direction']
}) {
  if (prior == null || now == null) return <span className="cfo-vc-move-track" aria-hidden="true" />
  const pos = (value: number) => Math.max(3, Math.min(97, value * 100))
  const priorPos = pos(prior)
  const nowPos = pos(now)
  const left = Math.min(priorPos, nowPos)
  const width = Math.abs(nowPos - priorPos)
  return (
    <span className="cfo-vc-move-track" aria-hidden="true">
      <i className={`cfo-vc-move-seg ${direction}`} style={{ left: `${left}%`, width: `${Math.max(width, 0.5)}%` }} />
      <i className="cfo-vc-move-dot prior" style={{ left: `${priorPos}%` }} />
      <i className={`cfo-vc-move-dot now ${direction}`} style={{ left: `${nowPos}%` }} />
    </span>
  )
}

/** Segment-CM-style movement grid: Line | Prior | movement | Now. Shows where
 *  each driver started and ended, not just its delta. Each row drills its own
 *  driver, same as the impact-bar list it upgrades. */
function MovementGrid({
  explanation,
  metricLabel,
  onAction,
  disabled = false,
}: {
  explanation: CfoInstantCardChangeExplanation
  metricLabel: string
  onAction?: (action: CfoInstantCard['actions'][number]) => void
  disabled?: boolean
}) {
  const rows = explanation.drivers.filter(hasMovementEndpoints).slice(0, 6)
  if (!rows.length) return null
  const detailRows = (explanation.detail?.drivers || []).filter(hasMovementEndpoints).slice(0, 4)
  return (
    <div className="cfo-vc-movegrid">
      <div className="cfo-vc-move-row cfo-vc-move-head" aria-hidden="true">
        <span>Line</span>
        <span className="num">{explanation.priorLabel ? `Prior · ${explanation.priorLabel}` : 'Prior'}</span>
        <span className="mid">{explanation.movementScaleLabel || 'Movement'}</span>
        <span className="num">{explanation.currentLabel ? `Now · ${explanation.currentLabel}` : 'Now'}</span>
      </div>
      {rows.map((driver) => {
        const action = driverInspectAction(driver, metricLabel)
        return (
          <button
            type="button"
            key={driver.id}
            className={`cfo-vc-move-row cfo-vc-move-line ${driver.direction}${driver.emphasis === 'total' ? ' total' : ''}`}
            onClick={() => action && onAction?.(action)}
            disabled={disabled || !action}
            aria-label={`Inspect ${driver.label}: ${driver.priorDisplay} to ${driver.currentDisplay} (${driver.value})`}
          >
            <span className="cfo-vc-move-name">
              <span>{driver.label}</span>
              {driver.subLabel ? <small>{driver.subLabel}</small> : null}
            </span>
            <span className="num prior">{driver.priorDisplay}</span>
            <span className="mid">
              <em className={`cfo-vc-move-delta ${driver.direction}`}>{driver.value}</em>
              <MovementDumbbell prior={driver.plotPrior} now={driver.plotCurrent} direction={driver.direction} />
            </span>
            <span className="num now">{driver.currentDisplay}</span>
          </button>
        )
      })}
      {detailRows.length ? (
        <>
          <div className="cfo-vc-move-detail-title">{explanation.detail?.label || 'Detail'}</div>
          {detailRows.map((driver) => {
            const action = driverInspectAction(driver, metricLabel)
            return (
              <button
                type="button"
                key={driver.id}
                className={`cfo-vc-move-row cfo-vc-move-line detail ${driver.direction}`}
                onClick={() => action && onAction?.(action)}
                disabled={disabled || !action}
                aria-label={`Inspect ${driver.label}: ${driver.priorDisplay} to ${driver.currentDisplay} (${driver.value})`}
              >
                <span className="cfo-vc-move-name"><span>{driver.label}</span></span>
                <span className="num prior">{driver.priorDisplay}</span>
                <span className="mid"><em className={`cfo-vc-move-delta ${driver.direction}`}>{driver.value}</em></span>
                <span className="num now">{driver.currentDisplay}</span>
              </button>
            )
          })}
        </>
      ) : null}
    </div>
  )
}

/** Ranked driver impact bars — the universal hero that replaces the redundant
 *  four-window "What changed" grid. Each row drills its own driver. */
function DriverImpactList({
  drivers,
  metricLabel,
  onAction,
  disabled = false,
}: {
  drivers: CfoInstantCardDriver[]
  metricLabel: string
  onAction?: (action: CfoInstantCard['actions'][number]) => void
  disabled?: boolean
}) {
  const rows = drivers.slice(0, 5)
  if (!rows.length) return null
  const maxAbs = Math.max(...rows.map((driver) => Math.abs(Number(driver.rawValue) || 0)), 1)
  return (
    <ul className="cfo-vc-drivers">
      {rows.map((driver) => {
        const width = Math.max(6, Math.round((Math.abs(Number(driver.rawValue) || 0) / maxAbs) * 100))
        const action = driverInspectAction(driver, metricLabel)
        const share = pctShare(driver.shareOfChange)
        return (
          <li key={driver.id}>
            <button
              type="button"
              className={`cfo-vc-driver ${driver.direction}`}
              onClick={() => action && onAction?.(action)}
              disabled={disabled || !action}
              aria-label={`Inspect ${driver.label}, ${driver.value}${share ? `, ${share} of the move` : ''}`}
            >
              <span className="cfo-vc-driver-name">{driver.label}</span>
              <span className="cfo-vc-driver-track" aria-hidden="true">
                <i style={{ width: `${width}%` }} />
              </span>
              <b className="cfo-vc-driver-val">{driver.value}</b>
              {share ? <span className="cfo-vc-driver-share">{share}</span> : null}
              <ChevronRight size={14} className="cfo-vc-driver-chevron" aria-hidden="true" />
            </button>
          </li>
        )
      })}
    </ul>
  )
}

/** Optional cause bridge (Prior NI → drivers → This NI). Rendered only when the
 *  surface carries true start/end anchors. */
function HeroBridge({
  bridge,
  drivers,
  metricLabel,
  onAction,
}: {
  bridge: NonNullable<CfoInstantCard['bridge']>
  drivers: CfoInstantCardDriver[]
  metricLabel: string
  onAction?: (action: CfoInstantCard['actions'][number]) => void
}) {
  const steps = drivers
    .filter((driver) => driver.rawValue != null && Number.isFinite(driver.rawValue))
    .map((driver) => ({ label: driver.label, delta: Number(driver.rawValue) }))
  if (steps.length < 2) return null
  return (
    <div className="cfo-vc-bridge">
      <WaterfallBridge
        className="cfo-vc-bridge-svg"
        ariaLabel={`Cause bridge for ${metricLabel}: ${bridge.start.label} to ${bridge.end.label}`}
        start={bridge.start}
        steps={steps}
        end={bridge.end}
        width={360}
        height={150}
        formatValue={(value) => compactMoney(value)}
        interactive
        onSelect={(selection) => {
          if (selection.kind !== 'element') return
          const driver = drivers.find((candidate) => trimStr(candidate.label).toLowerCase() === trimStr(selection.label).toLowerCase())
          const action = driver ? driverInspectAction(driver, metricLabel) : null
          if (action) onAction?.(action)
        }}
      />
    </div>
  )
}

/** Plain-language "what this is / why it matters / what good looks like" — the
 *  understandable-for-a-non-financial-reader read that leads the Explain modal. */
function ExplainBlock({ explain }: { explain?: CfoInstantCard['explain'] }) {
  if (!explain) return null
  const rows = [
    explain.whatItIs ? { k: 'What this is', v: explain.whatItIs } : null,
    explain.whyItMatters ? { k: 'Why it matters', v: explain.whyItMatters } : null,
    explain.goodRange ? { k: 'What good looks like', v: explain.goodRange } : null,
  ].filter(Boolean) as Array<{ k: string; v: string }>
  if (!rows.length && !explain.replacementLabel) return null
  return (
    <div className={`cfo-vc-explain${explain.isDataQuality ? ' is-data-quality' : ''}`}>
      {rows.map((row) => (
        <div className="cfo-vc-explain-row" key={row.k}>
          <span className="cfo-vc-explain-k">{row.k}</span>
          <span className="cfo-vc-explain-v">{row.v}</span>
        </div>
      ))}
      {explain.replacementLabel ? (
        <div className="cfo-vc-explain-swap">
          We focus on <b>{explain.replacementLabel}</b> instead — a better fit for this business.
        </div>
      ) : null}
    </div>
  )
}

function TrendComparisonStrip({ card }: { card: CfoInstantCard }) {
  const trendValues = (card.trend?.points || [])
    .map((point) => (point.rawValue != null && Number.isFinite(point.rawValue) ? Number(point.rawValue) : null))
    .filter((value): value is number => value != null)
  const latest = card.trend?.points?.[card.trend.points.length - 1]
  const start = card.trend?.points?.[0]
  const hasTrend = trendValues.length >= 2
  const lastIsDown = hasTrend ? trendValues[trendValues.length - 1] < trendValues[0] : false
  // Rows the contract couldn't fill are dropped, not rendered as "n/a" noise —
  // the agent still sees the full set (with reasons) via the compact card.
  const comparisons = (card.comparisons || []).filter((row) => !row.unavailableReason).slice(0, 4)
  // No apology when there's no dense series: the health gauge above already
  // carries the visual, so an empty strip simply doesn't render.
  if (!hasTrend && !comparisons.length) return null
  return (
    <div className="cfo-vc-context">
      {hasTrend ? (
        <div className="cfo-vc-trend">
          <div className="cfo-vc-strip-title">{card.trend?.label || '12-month trend'}</div>
          <Sparkline className="cfo-vc-spark" values={trendValues} down={lastIsDown} ariaLabel={card.trend?.label || '12-month trend'} />
          <div className="cfo-vc-trend-foot">
            <span>{start?.label || 'Start'}</span>
            <b>{latest?.value || ''}</b>
            <span>{latest?.label || 'Latest'}</span>
          </div>
        </div>
      ) : null}
      {comparisons.length ? (
        <table className="cfo-vc-compare">
          <thead>
            <tr><th>Period</th><th>Current</th><th>Prior</th><th>Δ</th></tr>
          </thead>
          <tbody>
            {comparisons.map((row) => (
              <tr key={row.label} className={row.unavailableReason ? 'missing' : undefined}>
                <td>{row.label}</td>
                {row.unavailableReason ? (
                  <td className="cfo-vc-compare-na" colSpan={3}>n/a</td>
                ) : (
                  <>
                    <td>{row.current}</td>
                    <td>{row.prior}</td>
                    <td className={row.tone || 'neutral'}>{row.change}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  )
}

const mergeActions = (
  primary: CfoInstantCard['actions'],
  extra: CfoInstantCard['actions'] | undefined,
): CfoInstantCard['actions'] => {
  const seen = new Set<string>()
  const out: CfoInstantCard['actions'] = []
  for (const action of [...(primary || []), ...(extra || [])]) {
    const label = trimStr(action?.label)
    const prompt = trimStr(action?.prompt)
    if (!label || !prompt) continue
    const key = `${trimStr(action.kind) || 'ask'}:${label.toLowerCase()}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(action)
  }
  return out
}

export function CfoInstantCardView({
  card,
  onPrompt,
  onAction,
  extraActions,
  disabled = false,
  driverControls = null,
}: {
  card: CfoInstantCard
  onPrompt?: (prompt: string) => void
  onAction?: (action: CfoInstantCard['actions'][number]) => void
  /** Warm-packet suggested options folded silently into the next-step chips. */
  extraActions?: CfoInstantCard['actions']
  disabled?: boolean
  /** Host-owned controls rendered next to the driver-strip title (e.g. the
   *  modal's comparison-period switcher and accounts ⇄ segments toggle). */
  driverControls?: ReactNode
}) {
  const explanation = card.changeExplanations?.[0] ?? null
  const drivers = explanation?.drivers ?? []
  const movementReady = !!explanation && drivers.some(hasMovementEndpoints)
  const verdict = card.verdict
  const grade = verdict?.grade ?? card.primaryMetric.tone ?? 'neutral'
  const showPill = grade === 'good' || grade === 'watch' || grade === 'risk'
  const insight = card.insights?.[0]
  const doNext = card.prescriptiveActions?.[0]
  const actions = mergeActions(card.nextSteps?.length ? card.nextSteps : card.actions, extraActions)
  const runAction = (action: CfoInstantCard['actions'][number]) => (onAction ? onAction(action) : onPrompt?.(action.prompt))
  return (
    <section className="cfo-instant-card cfo-vc" data-card-key={card.cardKey} data-grade={grade}>
      <header className={`cfo-vc-head grade-${grade}`}>
        {showPill ? <span className={`cfo-vc-grade grade-${grade}`}>{GRADE_LABEL[grade as NonNullable<CfoInstantCard['verdict']>['grade']]}</span> : null}
        <div className="cfo-vc-headline">
          <h3>{verdict?.headline || card.title}</h3>
          {/* The period badge on the right already shows the subtitle — only a real magnitude line earns a second row. */}
          {verdict?.magnitudeLine ? <p className="cfo-vc-mag">{verdict.magnitudeLine}</p> : null}
        </div>
        {card.subtitle ? <span className="cfo-vc-period"><Sparkles size={11} /> {card.subtitle}</span> : null}
      </header>

      {drivers.length ? (
        <div className="cfo-vc-hero">
          {card.bridge ? <HeroBridge bridge={card.bridge} drivers={drivers} metricLabel={card.title} onAction={onAction} /> : null}
          <div className="cfo-vc-driverwrap">
            <div className="cfo-vc-strip-head">
              <div className="cfo-vc-strip-title">{explanation?.label || "What's driving it"}</div>
              {driverControls}
            </div>
            {movementReady && explanation ? (
              <MovementGrid explanation={explanation} metricLabel={card.title} onAction={onAction} disabled={disabled} />
            ) : (
              <DriverImpactList drivers={drivers} metricLabel={card.title} onAction={onAction} disabled={disabled} />
            )}
          </div>
        </div>
      ) : card.healthBand ? (
        <div className="cfo-vc-gauge">
          <div className="cfo-vc-strip-head">
            <div className="cfo-vc-strip-title">Where this sits vs. healthy</div>
            {driverControls}
          </div>
          <HealthRangeBar
            className="cfo-vc-gauge-svg"
            value={card.healthBand.value}
            healthyMin={card.healthBand.healthyMin}
            healthyMax={card.healthBand.healthyMax}
            display={card.healthBand.display}
            minLabel={card.healthBand.minLabel}
            maxLabel={card.healthBand.maxLabel}
            assessment={card.healthBand.assessment}
            size="full"
          />
        </div>
      ) : (
        <div className="cfo-vc-skeleton">
          <div className="cfo-vc-strip-head">
            <span>Use the steps below to open the lines behind this number.</span>
            {driverControls}
          </div>
        </div>
      )}

      <ExplainBlock explain={card.explain} />

      <TrendComparisonStrip card={card} />

      {insight ? (
        <p className="cfo-vc-insight"><b>Insight</b> {insight}</p>
      ) : null}

      {actions.length ? (
        <div className="cfo-vc-next">
          {doNext ? <div className="cfo-vc-next-label">Do this next — {doNext}</div> : null}
          <div className="cfo-vc-actions">
            {actions.slice(0, 3).map((action, index) => (
              <button
                type="button"
                className={index === 0 ? 'primary' : ''}
                key={`${action.label}:${action.prompt}`}
                onClick={() => runAction(action)}
                disabled={disabled}
              >
                {action.kind === 'transactions' ? <Database size={13} /> : <ArrowRight size={13} />}
                {action.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  )
}
